// Brand × Vertical × Topic programmatic SEO engine for Fundle.ai
// Generates ~500 pages with real Indian + global brand mentions.
// Output is merged into allSEOPages so GuidePage.jsx + sitemap generator pick it up automatically.

// =================== DIMENSIONS ===================

const verticals = [
  {
    id: 'mens-fashion',
    name: "Men's Fashion",
    slug: 'mens-fashion-brands',
    visit: '4-6 times a year',
    aov: '₹1,800-3,500',
    margin: '40-55%',
    intro: "India's organised men's fashion market is roughly ₹1.2 lakh crore and growing at 11% CAGR. Buyers shop on occasion (weddings, festivals, work upgrades) and brand-switch quickly when not engaged between purchases.",
    pain: "Most men's fashion brands run a points programme that nobody redeems and a WhatsApp list nobody reads. The result: AOV is decent but visit frequency stays at 4-6 times a year forever.",
    brands: ['Levi\'s', 'Allen Solly', 'Louis Philippe', 'Park Avenue', 'U.S. Polo Assn.', 'Jack & Jones', 'Peter England', 'Van Heusen', 'Arrow', 'Blackberrys'],
    angle: 'style-based personalisation, occasion-driven engagement, fit-data + size memory across visits',
  },
  {
    id: 'womens-fashion',
    name: "Women's Fashion",
    slug: 'womens-fashion-brands',
    visit: '6-9 times a year',
    aov: '₹1,400-2,800',
    margin: '45-60%',
    intro: "Women's fashion is the largest organised apparel sub-segment in India — ~₹3 lakh crore — and is dominated by chains like Biba, W, Aurelia, Global Desi, AND, FabIndia, and Rangriti. Visit frequency is 6-9x a year, but most of that footfall stays anonymous.",
    pain: "Cataloguing 8-10 styles per visit and remembering nothing about it is the default state of women's-wear retail today. Brands burn ad spend re-acquiring the same shopper every season.",
    brands: ['Biba', 'W for Woman', 'Aurelia', 'AND', 'Global Desi', 'FabIndia', 'Rangriti', 'Soch', 'Vero Moda', 'Forever New'],
    angle: 'style-cluster recognition, festival/wedding occasion timing, size-fit memory, exclusive-preview tiers',
  },
  {
    id: 'indian-ethnic-wear',
    name: 'Indian Ethnic Wear',
    slug: 'indian-ethnic-wear-brands',
    visit: '3-5 times a year',
    aov: '₹3,500-12,000',
    margin: '45-65%',
    intro: 'Ethnic wear is a ₹1.8 lakh crore market with extreme seasonality — Diwali, Karwa Chauth, Eid and wedding season drive 60% of revenue. Brands like Manyavar, Mohey, Tasva, Soch and Twamev compete on bridal and groom-side market share.',
    pain: 'Most ethnic-wear retailers run discount-led acquisition every festival, then go silent for 4 months. Result: weak repeat behaviour and zero data on the bridal cohort beyond the wedding date.',
    brands: ['Manyavar', 'Mohey', 'Tasva', 'Soch', 'Twamev', 'Sangria', 'Biba', 'FabIndia', 'Sabyasachi', 'Anita Dongre'],
    angle: 'wedding-cohort marketing, festival cadence, family-account linking (groom + father + brothers), price-anchor management',
  },
  {
    id: 'footwear',
    name: 'Footwear Brands',
    slug: 'footwear-brands',
    visit: '3-5 times a year',
    aov: '₹1,600-4,500',
    margin: '40-55%',
    intro: 'India\'s organised footwear market is roughly ₹85,000 crore, split across athleisure (Skechers, Puma, Nike), value (Bata, Liberty), and lifestyle (Metro, Mochi, Crocs). Repeat behaviour is high in athleisure, near-zero in formal.',
    pain: 'Most footwear retailers do not track which silhouette a customer wears, what size, what occasion. The next visit is a blank slate every time.',
    brands: ['Skechers', 'Bata', 'Liberty', 'Metro Shoes', 'Mochi', 'Crocs', 'Puma', 'Nike', 'Adidas', 'Woodland'],
    angle: 'silhouette + size memory, replenishment timing (running shoes wear out at predictable mileage), occasion-based recommendations',
  },
  {
    id: 'pet-brands',
    name: 'Pet Brands',
    slug: 'pet-brands',
    visit: '12-18 times a year',
    aov: '₹900-2,400',
    margin: '25-40%',
    intro: 'India\'s pet-care market crossed ₹7,500 crore in 2025 and is growing at 14% CAGR. Brands like Heads Up For Tails, Drools, Royal Canin, Pedigree, Wiggles and Whiskas serve a hyper-loyal pet-parent base with monthly purchase cadence.',
    pain: 'Pet retail has the best repeat rate in Indian retail (10-18 visits/year) but the worst data capture. Most brands cannot tell you the breed, age, or weight of their customer\'s pet.',
    brands: ['Heads Up For Tails', 'Drools', 'Royal Canin', 'Pedigree', 'Whiskas', 'Wiggles', 'Farmina', 'Acana', 'Sheba', 'Himalaya Pet'],
    angle: 'pet-profile data (breed, age, weight, dietary needs), replenishment-cycle automation, vet-visit reminders, milestone campaigns (puppy → adult → senior)',
  },
  {
    id: 'beauty-cosmetics',
    name: 'Beauty & Cosmetics Brands',
    slug: 'beauty-cosmetics-brands',
    visit: '6-10 times a year',
    aov: '₹900-2,500',
    margin: '50-70%',
    intro: 'India\'s beauty-and-personal-care market is ₹1.7 lakh crore. Nykaa, MyGlamm, Mamaearth, Sugar, Plum and Lakme own the conversation; MAC and Sephora-stocked premium brands hold the upper end.',
    pain: 'Beauty buyers experiment constantly — 6-10 visits a year, often switching brand each time. Without skin-type / hair-type / shade data captured at first purchase, every campaign is a guess.',
    brands: ['Nykaa', 'MyGlamm', 'Mamaearth', 'Sugar Cosmetics', 'Plum', 'Lakmé', 'MAC', 'L\'Oréal Paris', 'Maybelline', 'The Body Shop'],
    angle: 'skin/hair-type profile, shade matching, replenishment cycle (mascara 3mo, foundation 6mo), influencer-driven campaign timing',
  },
  {
    id: 'fnb-restaurants',
    name: 'F&B & QSR Chains',
    slug: 'fnb-restaurant-chains',
    visit: '15-30 times a year',
    aov: '₹250-650',
    margin: '12-22%',
    intro: 'India\'s organised F&B market is ₹5.99 lakh crore. Haldiram\'s, Bikanervala, McDonald\'s, KFC, Domino\'s, Burger King, Wow Momo and Pizza Hut compete on speed, value and daily habit.',
    pain: 'Delivery aggregators (Swiggy, Zomato) own the customer relationship. Most QSRs have no idea who orders, how often, or what they\'d switch to.',
    brands: ['Haldiram\'s', 'Bikanervala', 'McDonald\'s', 'KFC', 'Domino\'s', 'Burger King', 'Pizza Hut', 'Wow! Momo', 'Subway', 'Starbucks'],
    angle: 'daypart targeting, weekly habit formation, aggregator-independence via direct-WhatsApp ordering, family-pack upsell',
  },
  {
    id: 'jewellery',
    name: 'Jewellery Brands',
    slug: 'jewellery-brands',
    visit: '1-3 times a year',
    aov: '₹35,000-2,00,000',
    margin: '12-25%',
    intro: 'India\'s organised jewellery market is ₹6.1 lakh crore and dominated by Tanishq, Kalyan, Malabar, Joyalukkas, CaratLane and Bluestone. Purchase frequency is 1-3x/year but ticket sizes are extreme.',
    pain: 'High-value customers see 12+ stores before a purchase. By the time they buy, the brand has no recollection of the buyer\'s preferences, family relationships or anniversary dates.',
    brands: ['Tanishq', 'Kalyan Jewellers', 'Malabar Gold', 'Joyalukkas', 'CaratLane', 'Bluestone', 'Tribhovandas Bhimji Zaveri', 'Reliance Jewels', 'PC Jeweller', 'Senco Gold'],
    angle: 'occasion-based campaigns (anniversary, child\'s birthday, festival), family-account linking, gold-price-trigger nudges, hand-feel preview booking',
  },
  {
    id: 'health-wellness',
    name: 'Health & Wellness Brands',
    slug: 'health-wellness-brands',
    visit: '8-18 times a year',
    aov: '₹600-1,800',
    margin: '15-30%',
    intro: 'India\'s organised pharmacy + wellness retail is ₹2.1 lakh crore. Apollo Pharmacy, MedPlus, Netmeds, 1mg and HealthKart dominate, with high purchase frequency from chronic-medication buyers.',
    pain: 'Pharmacies see the same customer every 30 days but treat each visit as a one-time transaction. Refill reminders are largely manual, leading to lapses and switches.',
    brands: ['Apollo Pharmacy', 'MedPlus', 'Netmeds', '1mg', 'HealthKart', 'GNC India', 'Wellness Forever', 'Pharmeasy', 'Truemeds', 'Practo Pharmacy'],
    angle: 'refill cycle automation, family chronic-medication tracking (consent-based), wellness-supplement upsell, doctor-visit follow-up',
  },
  {
    id: 'grocery-supermarket',
    name: 'Grocery & Supermarket Chains',
    slug: 'grocery-supermarket-chains',
    visit: '24-48 times a year',
    aov: '₹600-1,400',
    margin: '2-5%',
    intro: 'India\'s organised grocery retail is ₹1.2 lakh crore. DMart, Reliance Fresh, More, Spencer\'s, Star Bazaar and Big Bazaar (Smart Bazaar) drive the bulk. Visit frequency is the highest in retail — 24-48x/year.',
    pain: 'Grocery margins are 2-5%. Every basket leakage or substitution to a competitor costs real money. Most chains have no behavioural data per buyer.',
    brands: ['DMart', 'Reliance Fresh', 'More Retail', 'Spencer\'s Retail', 'Star Bazaar', 'Big Bazaar (Smart Bazaar)', 'Nature\'s Basket', 'Le Marche', 'Modern Bazaar', 'Vishal Mega Mart'],
    angle: 'basket-size optimisation, private-label cross-sell, category-leakage detection, price-elasticity per shopper',
  },
  {
    id: 'electronics',
    name: 'Electronics & Appliance Retail',
    slug: 'electronics-appliance-retail',
    visit: '2-4 times a year',
    aov: '₹6,500-45,000',
    margin: '8-18%',
    intro: 'India\'s organised electronics retail is ₹2.4 lakh crore, with Croma, Reliance Digital, Vijay Sales and Imagine Apple competing against e-commerce giants. Showrooming is the biggest problem.',
    pain: 'Buyer walks in, scans the barcode, buys it cheaper online. Without loyalty + service + financing-anchored relationships, physical electronics is a dying channel.',
    brands: ['Croma', 'Reliance Digital', 'Vijay Sales', 'Imagine Apple', 'Sangeetha Mobiles', 'Poorvika Mobiles', 'Bajaj Electronics', 'LOT Mobiles', 'Tata Croma', 'Girias'],
    angle: 'extended-warranty + AMC bundling, trade-in cycle automation, accessory cross-sell timing, financing-anchored loyalty',
  },
  {
    id: 'd2c-brands',
    name: 'D2C & E-Commerce Brands',
    slug: 'd2c-ecommerce-brands',
    visit: '4-8 times a year',
    aov: '₹900-3,500',
    margin: '35-65%',
    intro: 'India\'s D2C market is now ₹1.6 lakh crore. Lenskart, boAt, Wakefit, Mamaearth, Cult.Fit, Sleepy Owl, The Souled Store and Bewakoof have built brands directly on first-party data — yet most are now hitting CAC ceilings.',
    pain: 'D2C brands acquired customers cheaply on Meta + Google for 5 years. That arbitrage is gone. The brands that win the next 5 years will own a defensible first-party data + retention engine.',
    brands: ['Lenskart', 'boAt', 'Wakefit', 'Mamaearth', 'Cult.Fit', 'Sleepy Owl', 'The Souled Store', 'Bewakoof', 'SUGAR Cosmetics', 'Neeman\'s'],
    angle: 'subscription + replenishment cycle, post-purchase upsell windows, referral-loop optimisation, retention-driven LTV expansion',
  },
  {
    id: 'vending-machines',
    name: 'Vending Machines & Smart Retail Kiosks',
    slug: 'vending-machine-brands',
    visit: '40-120 times a year',
    aov: '₹50-300',
    margin: '20-40%',
    intro: 'India\'s organised vending and smart-retail-kiosk market is a fast-emerging ₹2,500+ crore segment. Players like Daalchini, Wendor, SmartQ, Nayya, and Cherise are deploying IoT-connected vending machines across offices, metros, malls, hospitals and gated communities. Daily-frequency, low-ticket purchases make it the highest-touch ecosystem in retail.',
    pain: 'Vending operators see the same buyer 40-120 times a year and capture almost nothing about them — no preference, no payment-history-driven offer, no replenishment-aware loyalty. Walk-up cash transactions still dominate; UPI is closing the data gap but no operator is yet running a unified loyalty layer.',
    brands: ['Daalchini', 'Wendor', 'SmartQ', 'Nayya', 'Cherise', 'Vendekin', 'Selecta', 'Crane', 'Atlas Vending', 'Kione'],
    angle: 'UPI-tap loyalty enrolment, weekly habit formation, micro-offers tied to time-of-day, predictive replenishment per machine, payment-data-driven personalisation',
  },
  {
    id: 'proptech',
    name: 'PropTech & Real Estate Technology',
    slug: 'proptech-real-estate',
    visit: '2-6 touches a year',
    aov: '₹40 L - ₹3 Cr',
    margin: '4-8%',
    intro: 'India\'s PropTech market is a ₹70,000+ crore segment spanning residential and commercial real estate, brokerage tech, and rental marketplaces. Brands like NoBroker, Magicbricks, Housing.com, 99acres, Square Yards, NoPaperForms, and Anarock work with the highest-LTV customer in retail — the homebuyer.',
    pain: 'PropTech operators capture millions of leads but convert them at <1.5%. The funnel is leaky because there is no engagement layer between site-visit and site-walk; loyalty is non-existent for post-purchase residents (who could be a 25-year LTV customer for ancillary services).',
    brands: ['NoBroker', 'Magicbricks', 'Housing.com', '99acres', 'Square Yards', 'Anarock', 'PropertyShare', 'Reloy', 'Strata', 'Smart Properties India'],
    angle: 'lead-nurture sequencing across 6-9 month consideration cycles, post-purchase resident engagement, broker-portal loyalty, ancillary-service cross-sell (interior, mortgage, moving), tenant-portal CRM',
  },
];

const topics = [
  {
    id: 'loyalty-program',
    label: 'Loyalty Program',
    template: (v) => ({
      h1: `Loyalty Programs for ${v.name}`,
      hook: `${v.name} sit on a unique combination of visit frequency (${v.visit}), AOV (${v.aov}) and contribution margin (${v.margin}). That mix demands a loyalty programme purpose-built for the segment — not a generic points-and-tiers template.`,
      mainAngle: 'how the right loyalty programme architecture changes the unit economics of the category',
      kpis: ['Member visit frequency lift', 'Member AOV lift vs. control', 'Tier upgrade velocity', 'Reward redemption rate', 'Member NPS vs. non-member'],
    }),
  },
  {
    id: 'campaign-management-roi',
    label: 'Campaign Management & ROI',
    template: (v) => ({
      h1: `Campaign Management & ROI for ${v.name}`,
      hook: `Most ${v.name.toLowerCase()} run blanket campaigns to their entire base, measure open rate, and call it done. That\'s not campaign management — that\'s spray-and-pray. Modern campaign ROI in this category requires propensity-matched control groups, channel-aware send-time optimisation, and incrementality measurement at the SKU level.`,
      mainAngle: 'how to attach a defensible ROI number to every campaign in this category',
      kpis: ['Incremental revenue per campaign (vs. matched control)', 'Cost per qualified conversation', 'Channel-mix optimisation', 'Time-to-result per campaign', 'Margin per ₹100 spent'],
    }),
  },
  {
    id: 'coupon-campaigns',
    label: 'Coupon & Promotional Campaigns',
    template: (v) => ({
      h1: `Coupon Campaigns & Promotional Strategy for ${v.name}`,
      hook: `Batch coupons train ${v.name.toLowerCase()} customers to wait for the next discount. Dynamic, personalised coupon campaigns — gated by RFM, behaviour, and propensity — flip that dynamic and protect margin.`,
      mainAngle: 'how to move from blanket coupons to dynamic, per-customer coupons that defend margin',
      kpis: ['Margin per redeemed coupon', 'Coupon-redemption rate by segment', 'Cannibalisation rate', 'Incremental visits per coupon delivered', 'Coupon → purchase latency'],
    }),
  },
  {
    id: 'pos-driven-campaigns',
    label: 'Point-of-Sale (POS) Driven Campaigns',
    template: (v) => ({
      h1: `Point-of-Sale (POS) Driven Campaigns for ${v.name}`,
      hook: `The till is the highest-intent moment a ${v.name.toLowerCase()} brand will ever have with a customer. Most brands waste it on a printed coupon and a "thank you ma\'am". POS-driven campaigns activate the till as a real-time decisioning surface.`,
      mainAngle: 'how to turn the POS into a real-time, member-aware decisioning surface that lifts AOV and frequency on every transaction',
      kpis: ['POS-prompted AOV lift', 'Next-visit conversion rate', 'Cashier-suggested upsell take rate', 'Real-time member identification rate at till', 'Cross-category basket lift'],
    }),
  },
  {
    id: 'first-party-data',
    label: 'First-Party Data Strategy',
    template: (v) => ({
      h1: `First-Party Data Strategy for ${v.name}`,
      hook: `Third-party cookies are gone. Meta and Google CPMs are up 40-60% in two years. For ${v.name.toLowerCase()} the only defensible long-term marketing asset is first-party data — collected at the till, on WhatsApp, in the app, and at the warehouse.`,
      mainAngle: 'how to build a first-party data engine that lowers CAC, raises LTV, and survives the cookie-less internet',
      kpis: ['% of transactions identified', 'Marketing-consent opt-in rate', 'CAC reduction year-over-year', 'LTV uplift on identified vs. anonymous buyers', 'Retargetable audience size'],
    }),
  },
  {
    id: 'customer-engagement',
    label: 'Customer Engagement (WhatsApp / RCS / Push)',
    template: (v) => ({
      h1: `Customer Engagement on WhatsApp, RCS & Push for ${v.name}`,
      hook: `97% open rate. 45-60% response rate. WhatsApp is the single biggest engagement opportunity for ${v.name.toLowerCase()} in India — and most brands are still spamming SMS with discount codes.`,
      mainAngle: 'how to build a real, sustained engagement programme on WhatsApp without burning out the customer',
      kpis: ['Marketing opt-in rate', 'WhatsApp-driven AOV lift', 'Cost per qualified conversation', '24-hour service-window utilisation', 'Aggregator-bypass rate (direct ordering)'],
    }),
  },
  {
    id: 'retention-churn',
    label: 'Customer Retention & Churn Reduction',
    template: (v) => ({
      h1: `Customer Retention & Churn Reduction in ${v.name}`,
      hook: `${v.name} typically spend 3-7x more on acquisition than retention. The brands that win year-on-year are the ones that flip that ratio — proactive, predictive, personalised retention is the highest-ROI marketing in this category.`,
      mainAngle: 'how to predict churn 30 days early and trigger automated win-back that actually works',
      kpis: ['30-day churn rate', 'Win-back conversion rate', 'Repeat-visit rate by cohort', 'LTV by acquisition cohort', '% of revenue from retained customers'],
    }),
  },
];

// Real mall list (public + Fundle ecosystem)
const malls = [
  { id: 'phoenix-marketcity-mumbai', name: 'Phoenix Marketcity Mumbai', city: 'Mumbai', footfall: '20 lakh/month', brands: 600, anchors: 'Lifestyle, Shoppers Stop, PVR, Hamleys' },
  { id: 'select-citywalk-delhi', name: 'Select CITYWALK Delhi', city: 'Delhi (Saket)', footfall: '15 lakh/month', brands: 200, anchors: 'Zara, H&M, Marks & Spencer, PVR' },
  { id: 'dlf-mall-of-india-noida', name: 'DLF Mall of India', city: 'Noida', footfall: '18 lakh/month', brands: 330, anchors: 'Hamleys, Lifestyle, PVR, Smaaash' },
  { id: 'inorbit-mall-malad', name: 'Inorbit Mall Malad', city: 'Mumbai', footfall: '12 lakh/month', brands: 200, anchors: 'Lifestyle, Shoppers Stop, PVR' },
  { id: 'lulu-mall-kochi', name: 'Lulu Mall Kochi', city: 'Kochi', footfall: '25 lakh/month', brands: 300, anchors: 'LuLu Hypermarket, PVR, Marks & Spencer' },
  { id: 'forum-mall-bangalore', name: 'Forum Mall Bangalore', city: 'Bangalore (Koramangala)', footfall: '12 lakh/month', brands: 150, anchors: 'Lifestyle, Shoppers Stop, PVR' },
  { id: 'quest-mall-kolkata', name: 'Quest Mall Kolkata', city: 'Kolkata', footfall: '8 lakh/month', brands: 100, anchors: 'Westside, Pantaloons, Inox' },
  { id: 'ambience-mall-gurgaon', name: 'Ambience Mall Gurgaon', city: 'Gurgaon', footfall: '14 lakh/month', brands: 230, anchors: 'Shoppers Stop, Lifestyle, PVR' },
  { id: 'pacific-mall-delhi', name: 'Pacific Mall Tagore Garden', city: 'Delhi', footfall: '10 lakh/month', brands: 200, anchors: 'Shoppers Stop, Reliance Trends' },
  { id: 'nexus-seawoods-mumbai', name: 'Nexus Seawoods', city: 'Navi Mumbai', footfall: '10 lakh/month', brands: 220, anchors: 'Westside, Lifestyle, Inox' },
  { id: 'orion-mall-bangalore', name: 'Orion Mall Bangalore', city: 'Bangalore (Brigade Gateway)', footfall: '11 lakh/month', brands: 180, anchors: 'PVR, Lifestyle, Marks & Spencer' },
  { id: 'phoenix-palladium-mumbai', name: 'Phoenix Palladium', city: 'Mumbai (Lower Parel)', footfall: '11 lakh/month', brands: 150, anchors: 'Burberry, Diesel, Hugo Boss, PVR' },
  { id: 'r-city-mall-mumbai', name: 'R City Mall Ghatkopar', city: 'Mumbai', footfall: '12 lakh/month', brands: 250, anchors: 'Lifestyle, Shoppers Stop, INOX' },
  { id: 'vr-mall-chennai', name: 'VR Chennai', city: 'Chennai', footfall: '10 lakh/month', brands: 200, anchors: 'Lifestyle, Marks & Spencer, PVR' },
  { id: 'sarath-city-capital-mall-hyderabad', name: 'Sarath City Capital Mall', city: 'Hyderabad', footfall: '14 lakh/month', brands: 230, anchors: 'Lifestyle, Shoppers Stop, INOX' },
];

// =================== PAGE BUILDERS ===================

function generateVerticalTopicPage(v, t) {
  const body = t.template(v);
  const slug = `${t.id}-for-${v.slug}`;
  const brandsLine = v.brands.slice(0, 6).join(', ');
  const brandsLater = v.brands.slice(6).join(', ');
  return {
    slug,
    title: `${body.h1} — Brand-Anchored Playbook`,
    metaDescription: `${body.h1} explained for retail brands like ${v.brands.slice(0, 4).join(', ')} and ${v.brands[4]}. Visit frequency ${v.visit}, AOV ${v.aov}, contribution margin ${v.margin}. Built by Fundle.ai's strategy team.`,
    metaKeywords: `${t.label.toLowerCase()} ${v.name.toLowerCase()}, ${v.name.toLowerCase()} loyalty, ${v.brands[0].toLowerCase()} loyalty, ${v.brands[1].toLowerCase()} loyalty, ${v.brands[2].toLowerCase()} loyalty, retail ${t.id.replace(/-/g, ' ')}, Indian ${v.name.toLowerCase()} brands, loyalty platform ${v.id}`,
    h1: body.h1,
    subtitle: `${v.intro}`,
    sections: [
      { heading: `Why ${t.label.toLowerCase()} matters in ${v.name.toLowerCase()}`, content: `${body.hook}\n\nIn this category — ${brandsLine}${brandsLater ? `, ${brandsLater}` : ''} — ${body.mainAngle}. The brands that take this seriously protect margin, raise frequency, and quietly compound. The brands that don\'t spend the next five years chasing CAC.`, type: 'text' },
      { heading: `The unit economics of ${v.name.toLowerCase()}`, content: `${v.pain}\n\nA realistic ${v.name.toLowerCase()} retailer in India runs the following profile today:`, type: 'text' },
      { heading: 'Category benchmarks', items: [
        `Visit frequency: ${v.visit}`,
        `Average order value (AOV): ${v.aov}`,
        `Contribution margin: ${v.margin}`,
        `Top angle to operate on: ${v.angle}`,
      ], type: 'list' },
      { heading: `How leading ${v.name.toLowerCase()} apply this`, content: `Across India, brands like ${brandsLine} are at different points on this maturity curve. Some are still running a static points programme that nobody redeems. Others — usually the D2C-native operators — have invested in a real first-party data and engagement stack and are seeing it compound.\n\nFundle.ai works with retail brands and mall ecosystems across this exact category and ships AI-native loyalty + engagement + first-party data + retail-media monetisation as one connected platform — built for Indian retail, in India, on Indian POS and Indian channels (WhatsApp, RCS, Indian-language SMS).`, type: 'text' },
      { heading: 'The Fundle playbook for this category', items: [
        `AI Loyalty Engine tuned to the ${v.name.toLowerCase()} purchase cadence`,
        'Real-time member identification at the POS (50+ pre-built Indian POS connectors)',
        'WhatsApp BSP (Meta-approved) for direct customer engagement at 97% open rate',
        'Automated propensity-matched control groups on every campaign — defensible ROI numbers',
        'Brand-side cohort intelligence: discover who your champions actually are',
        'Retail-media monetisation: turn your first-party data into a revenue line',
      ], type: 'list' },
      { heading: `KPIs we move on ${v.name.toLowerCase()} deployments`, items: body.kpis, type: 'list' },
      { heading: 'A starting point', content: `If you operate in this category and are running on a points programme nobody loves, a WhatsApp list nobody reads, and Meta + Google ads where the CAC keeps climbing — Fundle\'s strategy team will run a 60-minute audit of your current setup, free, and share back specific opportunities. No deck, no sales pitch.`, type: 'text' },
    ],
    relatedLinks: ['/contact', '/platform/loyalty-engine', '/platform/whatsapp-sms-rcs-marketing', '/solutions/brand-loyalty-platform'],
    category: v.name,
  };
}

function generateBrandSpecificPage(v, brand) {
  const brandSlug = brand.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const slug = `loyalty-program-like-${brandSlug}`;
  return {
    slug,
    title: `Loyalty Program Like ${brand} — How ${v.name} Build Repeat Behaviour`,
    metaDescription: `What a ${brand}-style loyalty programme actually looks like under the hood — and how Fundle.ai builds the same architecture for ${v.name.toLowerCase()} in India. Visit frequency ${v.visit}, AOV ${v.aov}.`,
    metaKeywords: `${brand} loyalty program, ${brand} loyalty, loyalty program like ${brand}, ${v.name.toLowerCase()} loyalty, ${brand} customer engagement, ${brand} alternative loyalty`,
    h1: `Build a loyalty programme like ${brand}`,
    subtitle: `${v.intro}`,
    sections: [
      { heading: `Why ${brand} keeps coming up in this category`, content: `${brand} sits inside India\'s ${v.name.toLowerCase()} category alongside ${v.brands.filter((b) => b !== brand).slice(0, 5).join(', ')} and others. Inside this group, the brands that are quietly building defensible repeat behaviour share a small number of common moves — and ${brand} is usually one of the brands operators try to benchmark against.\n\nThis page is not an endorsement of ${brand} and is not a comment on the specifics of ${brand}\'s internal stack. It is a category-level look at what good loyalty architecture in ${v.name.toLowerCase()} looks like — and what it would take to build it for your brand.`, type: 'text' },
      { heading: `What a ${brand}-style loyalty programme actually does`, items: [
        `Recognises the same customer across web, app, and store — every time, sub-300ms`,
        `Captures ${v.angle.split(',')[0].trim()} from the first transaction onward`,
        `Earns trust before asking for opt-in (no upfront 10-question form)`,
        `Targets the next campaign on real behavioural cohorts, not "all members"`,
        `Operates on WhatsApp as the primary channel — not as an afterthought`,
        `Closes the loop with incrementality measurement (matched control groups)`,
      ], type: 'list' },
      { heading: `Why this matters for ${v.name.toLowerCase()} specifically`, content: `${v.pain}\n\nIn a category where visit frequency is ${v.visit} and AOV is ${v.aov}, the gap between brands with this architecture and brands without it widens every year. Acquisition keeps getting more expensive; retention is the only sustainable lever left.`, type: 'text' },
      { heading: `Brands in this category Fundle works with`, content: `Fundle.ai works with retail brands and mall ecosystems across ${v.name.toLowerCase()} — the same category as ${v.brands.slice(0, 5).join(', ')} and ${v.brands[5] || v.brands[0]}. We ship loyalty + engagement + first-party data + retail-media monetisation as one platform, deployed in 4-6 weeks on Indian POS, Indian channels (WhatsApp, RCS, Indian-language SMS), and INR pricing.`, type: 'text' },
      { heading: 'The Fundle stack at a glance', items: [
        'AI Loyalty Engine — RFM, churn prediction, automated tier management',
        'AI Campaign Agent — propensity-matched control groups, send-time optimisation',
        'WhatsApp BSP (Meta-approved) — 97% open rate, two-way conversation, bill-scan',
        'Customer 360 — unified profile across stores, app, web, support',
        'Retail Media Network — monetise the audience you already have',
        '50+ pre-built Indian POS connectors',
      ], type: 'list' },
      { heading: 'How to evaluate this for your brand', content: `Look at three numbers in your business: (1) % of transactions identified to a known member, (2) marketing opt-in rate at enrolment, (3) incremental revenue per campaign measured against a matched control. If any of these are missing or under 30%, you have a loyalty-architecture gap — not a creative gap.\n\nFundle\'s strategy team runs a 60-minute audit on these three numbers for ${v.name.toLowerCase()} brands, free, and shares back a tight set of opportunities. Email hello@fundle.ai or use the contact form.`, type: 'text' },
    ],
    relatedLinks: ['/contact', `/guide/loyalty-program-for-${v.slug}`, '/platform/loyalty-engine', '/platform/whatsapp-sms-rcs-marketing'],
    category: v.name,
  };
}

function generateMallPage(m) {
  return {
    slug: `loyalty-program-for-${m.id}`,
    title: `Loyalty & Customer Engagement Programmes for ${m.name}`,
    metaDescription: `How a modern coalition loyalty + first-party-data programme works for a mall ecosystem like ${m.name} (${m.city}) — ${m.footfall} footfall, ${m.brands}+ brands. Built by Fundle.ai.`,
    metaKeywords: `${m.name} loyalty, ${m.city} mall loyalty, mall loyalty program, coalition loyalty, retail media mall, ${m.name} customer engagement, ${m.name} first party data`,
    h1: `Loyalty & customer engagement for a mall like ${m.name}`,
    subtitle: `Roughly ${m.footfall} of footfall. ${m.brands}+ brands inside one ecosystem. Anchored by ${m.anchors}. This is what the modern playbook for a mall of this scale looks like.`,
    sections: [
      { heading: 'Why malls are the next first-party data wave', content: `Malls have what individual brands cannot easily build alone: identified, repeat, multi-category shoppers, visiting under one roof. A mall like ${m.name} sees ${m.footfall} of footfall — that\'s a top-1% audience in Indian retail. Most malls have squandered this advantage by running points programmes that don\'t talk to the tenants, can\'t measure brand-level revenue contribution, and don\'t feed into a retail-media monetisation layer.\n\nThe next-generation mall loyalty programme is a media network with a loyalty layer on top — not the other way around.`, type: 'text' },
      { heading: `The four engines of a modern mall programme`, items: [
        'WhatsApp bill-scan as the front door — far higher conversion than kiosks; gives you transaction data on every visit',
        'Footfall + dwell analytics tied to identified shoppers — Fundle ADSR matches camera/Wi-Fi data to enrolled visitors',
        'Brand revenue-share programmes — charge tenant brands per qualified customer delivered, not just per square foot',
        `Retail media network — sell tenant brand campaigns inside the mall\'s WhatsApp / app / digital signage, with cohort targeting`,
      ], type: 'list' },
      { heading: `Specific to ${m.name}`, content: `A mall like ${m.name} — anchored by ${m.anchors}, with ${m.brands}+ tenant brands and ${m.footfall} of footfall — has the kind of audience scale where each percentage-point of conversion lift translates to crores of annual revenue, both at the mall level and at the tenant brand level.\n\nFundle.ai works with mall operators across India on exactly this playbook: WhatsApp bill-scan + first-party data + tenant retail-media + footfall analytics. Deployment is 4-6 weeks. Operators typically see 60-70% of footfall enrolled within 6 months and a measurable retail-media revenue line within 12 months.`, type: 'text' },
      { heading: 'How to start', items: [
        'Audit your current programme: enrolment %, opt-in %, attribution to tenant revenue',
        'Pilot bill-scan in 1-2 high-footfall zones for 90 days',
        'Layer footfall + dwell analytics for identified shoppers',
        'Open the tenant retail-media product to 5-10 anchor brands',
        'Move to full ecosystem rollout in months 6-12',
      ], type: 'list' },
      { heading: 'Talk to Fundle', content: `Fundle\'s mall team runs a free 60-minute audit for malls like ${m.name}. We share back a specific 90-day pilot plan with numbers, not a slide deck. Email hello@fundle.ai or use the contact form.`, type: 'text' },
    ],
    relatedLinks: ['/contact', '/solutions/mall-loyalty-platform', '/platform/retail-media-network', '/platform/fundle-brain'],
    category: 'Malls',
  };
}

function generateD2CPage(d2c) {
  return {
    slug: d2c.slug,
    title: `${d2c.title}`,
    metaDescription: d2c.desc,
    metaKeywords: d2c.keywords,
    h1: d2c.title,
    subtitle: d2c.subtitle,
    sections: d2c.sections,
    relatedLinks: d2c.related || ['/contact', '/platform/loyalty-engine', '/platform/whatsapp-sms-rcs-marketing'],
    category: 'D2C & E-Commerce',
  };
}

// =================== ASSEMBLE ===================

export function buildBrandVerticalPages() {
  const pages = {};

  // Tier 1+2: Vertical × Topic pillar pages (12 × 7 = 84)
  verticals.forEach((v) => {
    topics.forEach((t) => {
      const p = generateVerticalTopicPage(v, t);
      pages[p.slug] = p;
    });
  });

  // Tier 3: Brand-specific pages (≈ 12 × 10 = 120)
  verticals.forEach((v) => {
    v.brands.forEach((brand) => {
      const p = generateBrandSpecificPage(v, brand);
      pages[p.slug] = p;
    });
  });

  // Tier 4: Mall-specific pages (15)
  malls.forEach((m) => {
    const p = generateMallPage(m);
    pages[p.slug] = p;
  });

  // Tier 5: D2C-specific deep pages (10)
  const d2cPages = [
    {
      slug: 'd2c-loyalty-programs-india',
      title: 'D2C Loyalty Programs in India — Beyond CAC',
      desc: 'How Indian D2C brands like Lenskart, boAt, Wakefit and Mamaearth move from CAC-driven growth to retention-driven LTV. The Fundle.ai playbook for D2C loyalty.',
      keywords: 'D2C loyalty India, D2C loyalty program, Lenskart loyalty, boAt loyalty, Mamaearth loyalty, Indian D2C retention',
      subtitle: 'For five years, Indian D2C was built on cheap Meta CPMs. That era is over. The brands that win the next five years will own a defensible first-party retention engine.',
      sections: [
        { heading: 'The end of cheap acquisition', content: 'India\'s top D2C brands — Lenskart, boAt, Mamaearth, Wakefit, Cult.Fit, Sleepy Owl — were all built on a Meta + Google performance-marketing arbitrage that compounded for five years. That window has closed. Meta and Google CPMs are up 40-60% in two years; iOS ATT removed the cleanest attribution; and the next billion buyers cost more, not less, to reach.', type: 'text' },
        { heading: 'What changes in D2C loyalty', items: [
          'Retention becomes the single most defensible marketing asset',
          'Subscription + replenishment-cycle loyalty replaces points-and-tiers',
          'Post-purchase upsell windows (D+3, D+14, D+45) become campaign-grade',
          'WhatsApp becomes the primary CRM channel, not a notification pipe',
          'First-party data becomes a P&L line via cohort cross-sell + retail-media partnerships',
        ], type: 'list' },
        { heading: 'How Fundle.ai works with D2C brands', content: 'Fundle is the AI loyalty + engagement + first-party data + retail-media platform for retail brands and mall ecosystems. For D2C operators it ships as one connected stack: AI loyalty (RFM, churn prediction), AI campaign agent (propensity-matched control groups), WhatsApp BSP (97% open rate), and Customer 360 — deployed in 4-6 weeks on Shopify, WooCommerce, or any custom storefront.', type: 'text' },
      ],
      related: ['/contact', '/loyalty-program-for-d2c-ecommerce-brands', '/platform/whatsapp-sms-rcs-marketing'],
    },
    {
      slug: 'd2c-campaign-management-roi',
      title: 'D2C Campaign Management & ROI — Indian Brands Playbook',
      desc: 'How leading Indian D2C brands run campaigns with defensible ROI: propensity-matched control groups, send-time optimisation, SKU-level incrementality, and aggregator-independence.',
      keywords: 'D2C campaign management, D2C ROI, D2C marketing ROI India, Indian D2C campaign strategy',
      subtitle: 'Most D2C teams measure open rate and revenue inside the campaign window — and call it ROI. That\'s reach, not ROI. Here\'s what defensible D2C campaign ROI actually looks like.',
      sections: [
        { heading: 'Why traditional D2C campaign measurement breaks', content: 'When you measure "revenue inside the campaign window", you count revenue that would have happened anyway. The D2C brands that survive the next five years are the ones that measure incremental revenue against a matched control group — not gross campaign revenue.', type: 'text' },
        { heading: 'The Fundle ROI model for D2C campaigns', items: [
          'Propensity-matched control: at every send, hold out 5-10% who would have received the message',
          'Measure 14-day incremental revenue vs. control — that\'s the real number',
          'Subtract programme cost (template fees + reward cost + tech)',
          'Net incremental margin = the actual P&L impact',
          'Roll up to channel-mix and campaign-type for budget allocation',
        ], type: 'list' },
        { heading: 'KPIs that matter', items: [
          'Incremental revenue per campaign (vs. matched control)',
          'Cost per qualified conversation',
          'Margin per ₹100 of campaign spend',
          'Time-to-result per campaign',
          '24-hour service-window utilisation',
        ], type: 'list' },
      ],
      related: ['/contact', '/campaign-management-roi-for-d2c-ecommerce-brands', '/platform/campaign-automation'],
    },
    {
      slug: 'd2c-first-party-data-strategy',
      title: 'First-Party Data Strategy for D2C & E-Commerce Brands',
      desc: 'Why D2C brands win or lose on the strength of their first-party data engine — and how Fundle.ai builds one for Indian D2C in 4-6 weeks.',
      keywords: 'D2C first party data, D2C data strategy, D2C CDP, D2C customer data platform, Indian D2C data',
      subtitle: 'Third-party cookies are gone. The brands that own their customer data own their cost of marketing. The ones that don\'t are renting it from Meta and Google forever.',
      sections: [
        { heading: 'The four signals every D2C brand should be capturing', items: [
          'Purchase: SKU, frequency, basket composition, return behaviour',
          'Engagement: app opens, push response, WhatsApp clicks, email opens',
          'Intent: site behaviour, cart abandonment, browse patterns',
          'Identity: phone, email, address, ATR (across-the-relationship) joins',
        ], type: 'list' },
        { heading: 'How Fundle stitches these together', content: 'Fundle\'s Customer 360 module unifies these four signals into a single live profile per customer, exposes it through a natural-language query interface (Fundle Brain), and makes it actionable in every campaign and every loyalty rule. No data lake project. No 6-month CDP rollout. Deployed in 4-6 weeks.', type: 'text' },
      ],
      related: ['/contact', '/first-party-data-for-d2c-ecommerce-brands', '/platform/customer-360'],
    },
    {
      slug: 'd2c-customer-engagement-whatsapp',
      title: 'D2C Customer Engagement on WhatsApp — Beyond Notifications',
      desc: 'How D2C brands like Lenskart, Mamaearth and boAt are using WhatsApp as their primary CRM channel — not just a notification pipe. The Fundle.ai playbook.',
      keywords: 'D2C WhatsApp marketing, WhatsApp D2C engagement, D2C customer engagement, Indian D2C WhatsApp',
      subtitle: '97% open rate. 45-60% response rate. Two-way conversation. WhatsApp is the highest-fidelity channel any D2C brand will ever have — and most brands still use it like SMS with a logo.',
      sections: [
        { heading: 'What WhatsApp-first D2C engagement actually looks like', items: [
          'Order placed → confirmation + delivery tracker',
          'Delivered → product-care tip in 24 hours',
          'D+14 → review nudge with photo upload',
          'D+30 → cohort-specific cross-sell',
          'D+45 → replenishment trigger',
          'D+90 → upsell to subscription / refer-a-friend',
        ], type: 'list' },
        { heading: 'Compliance basics every D2C team must get right', items: [
          'Use only Meta-approved template categories (UTILITY, MARKETING, AUTH)',
          'Marketing opt-in is explicit (checkbox + clear copy)',
          'Honour opt-outs inside 24 hours',
          'No marketing between 10pm and 8am',
          'Stay inside the 24-hour service window from last customer reply',
        ], type: 'list' },
      ],
      related: ['/contact', '/customer-engagement-for-d2c-ecommerce-brands', '/platform/whatsapp-sms-rcs-marketing'],
    },
    {
      slug: 'd2c-retention-churn-reduction',
      title: 'Reducing Churn in D2C — Predictive Retention That Actually Works',
      desc: 'How Indian D2C brands use AI-powered churn prediction + automated win-back to lift 12-month LTV by 20-40%. The Fundle.ai retention playbook.',
      keywords: 'D2C retention, D2C churn reduction, D2C customer retention India, D2C LTV',
      subtitle: 'D2C brands burn 3-7x more on acquisition than retention. The ones that flip that ratio compound. The rest spend the next five years chasing CAC.',
      sections: [
        { heading: 'Why most win-back campaigns fail', content: 'Win-back campaigns triggered on day 90 of inactivity convert at <10%. The customer has already moved on emotionally and behaviourally. The brands that win retention trigger interventions on day 30 of declining behaviour — not on day 90 of full inactivity.', type: 'text' },
        { heading: 'The Fundle retention agent', items: [
          'Daily churn risk score for every member (0-100)',
          'Behavioural signals: declining visit frequency, shrinking basket, narrowing category breadth',
          'Triggered intervention library: WhatsApp message, personalised coupon, premium tier offer',
          'Automated A/B testing of interventions per segment',
          'Real-time outcome tracking (did the win-back stick at 30 / 60 / 90 days?)',
        ], type: 'list' },
      ],
      related: ['/contact', '/retention-churn-for-d2c-ecommerce-brands', '/platform/fundle-brain'],
    },
  ];
  d2cPages.forEach((d) => { pages[d.slug] = generateD2CPage(d); });

  return pages;
}

export const brandVerticalPages = buildBrandVerticalPages();
export const brandVerticalSlugs = Object.keys(brandVerticalPages);
export const brandVerticalCount = brandVerticalSlugs.length;

// Group pages for the Resource Compass discovery icon
export const brandVerticalIndex = {
  verticals: verticals.map((v) => ({
    id: v.id,
    name: v.name,
    slug: v.slug,
    topics: topics.map((t) => ({ id: t.id, label: t.label, slug: `${t.id}-for-${v.slug}` })),
    brands: v.brands.slice(0, 8).map((b) => ({
      name: b,
      slug: `loyalty-program-like-${b.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`,
    })),
  })),
  malls: malls.map((m) => ({ id: m.id, name: m.name, city: m.city, slug: `loyalty-program-for-${m.id}` })),
  d2c: [
    { slug: 'd2c-loyalty-programs-india', title: 'D2C Loyalty Programs in India' },
    { slug: 'd2c-campaign-management-roi', title: 'D2C Campaign Management & ROI' },
    { slug: 'd2c-first-party-data-strategy', title: 'D2C First-Party Data Strategy' },
    { slug: 'd2c-customer-engagement-whatsapp', title: 'D2C Customer Engagement on WhatsApp' },
    { slug: 'd2c-retention-churn-reduction', title: 'D2C Retention & Churn Reduction' },
  ],
};
